import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { LoyaltyCardsType, TravellerCardValues } from '../../../../../../Loyalty/types';
import { Log } from '../../../../../../sentry';
import { useConfig } from '../../../../../../context';
import { useConfigRedirects, useToggleable } from '../../../../../../hooks';
import { isExistI18nTranslate } from '../../../../../../utils';
import { useTheme } from '../../../../../../theme';
import { getUserInfo } from '../../../../../../Checkout/utils';
import { formatError, getCorporateNumber } from '../../../../../../Loyalty/utils';
import { fillOrder } from '../../../../../../Checkout/store/order/actions';
import { useCardExtras } from '../../../../../../CardExtra';
import Button from '../../../../../../BaseComponents/Button';
import LinkCardsModal from '../../../../../../Loyalty/components/LinkCardsModal/LinkCardsModal';
import LoyaltyConnect from '../../../../../../Loyalty/components/LoyaltyStub/LoyaltyConnect/LoyaltyConnect';
import cn from 'classnames';

import { InputTraveller, TravellerFieldEnum, useAddTravellersDataMutation } from '@websky/graphql';
import {
	SelectedOrderServiceStatus,
	OrderServiceCardSummaryService,
	OrderServiceSummarySelectedItem,
	useOrderSelectors,
	getTravellers
} from '@entities/order';
import {
	CardExtraView,
	CardExtraViewAggregator,
	CardExtraIcon,
	CardExtraSummaryGroup,
	CardExtraSummaryItem,
	CardExtraAggregatorHeaderIcon
} from '@shared/ui';

interface Props {
	variant?: 'default' | 'aggregator';
}

const LoyaltyOrderCard: React.FC<Props> = ({ variant }) => {
	const { t } = useTranslation('CardExtra');
	const dispatch = useDispatch();
	const { Loyalty: loyaltyTheme } = useTheme('CheckoutAdditionalService');

	const {
		global: {
			companyInfo: { loyaltyProgramName, loyaltyProgramAccountUrl }
		}
	} = useConfig();

	const selectors = useOrderSelectors();
	const orderId = useSelector(selectors.getOrderId);
	const travellersWithLoyalty = useSelector(selectors.getFfpBonusByTravellers);
	const milesBonus = useSelector(selectors.getFfpBonusForFlight);
	const isFfpSupported = useSelector(selectors.getIsFfpSupported);
	const orderTravellers = useSelector(selectors.getTravellers);
	const travellers = useSelector(getTravellers);

	const [formError, setFormError] = useState('');
	const { isOpen: isOpenLoyalty, open: onOpenLoyalty, close: onCloseLoyalty } = useToggleable(false);
	const { isOpen: isOpenLinkCards, open: onOpenLinkCards, close: onCloseLinkCards } = useToggleable(false);

	const { userInfo, updateUser } = useCardExtras();
	const { onGoToAccountSettings } = useConfigRedirects();

	const [setTravellersData, { loading }] = useAddTravellersDataMutation();

	const {
		summaryServices,
		orderTravellersWithLoyalty,
		isFilledAllLoyaltyCards,
		isSelectedLoyaltyCards
	} = useMemo(() => {
		const statusSet = new Set<SelectedOrderServiceStatus>();

		const summaryServices = Object.entries(travellersWithLoyalty).map<OrderServiceCardSummaryService>(
			([travellerId, travellerCardFfp], index) => {
				const status = travellerCardFfp.cardNumber ? 'confirmed' : 'booked';
				statusSet.add(status);

				const commonServiceProps: Pick<
					OrderServiceSummarySelectedItem,
					'travellerId' | 'serviceId' | 'segmentIds' | 'isLoading'
				> = {
					serviceId: `loyalty-${index}`,
					segmentIds: [],
					travellerId
				};

				const selectedServices: OrderServiceSummarySelectedItem[] = [
					{
						...commonServiceProps,
						status,
						icon: 'Action/Payment',
						label: travellerCardFfp.cardNumber || t('EmptyCard')
					}
				];

				if (travellerCardFfp.bonusForFlight) {
					selectedServices.push({
						...commonServiceProps,
						status: 'booked',
						icon: 'Services/Miles',
						label: t('PointsWillBeCredited'),
						price: { currency: '', amount: travellerCardFfp.bonusForFlight }
					});
				}

				return {
					selectedServices,
					travellerLabel: travellers[travellerId]?.values.fullName,
					isAllSegments: false
				};
			}
		);

		return {
			summaryServices,
			orderTravellersWithLoyalty: orderTravellers.filter(traveller => travellersWithLoyalty[traveller.id]),
			isFilledAllLoyaltyCards: statusSet.size === 1 && statusSet.has('confirmed'),
			isSelectedLoyaltyCards: statusSet.size > 0
		};
	}, [travellers, orderTravellers, travellersWithLoyalty]);

	const content = (!milesBonus || summaryServices.length > 0) && (
		<>
			{!milesBonus && <span>{t('StartSavingNow')}</span>}
			{summaryServices.length > 0 && (
				<div>
					{summaryServices.map(summary => (
						<CardExtraSummaryGroup key={summary.travellerLabel} travellerLabel={summary.travellerLabel}>
							{summary.selectedServices.map((selectedService, index) => (
								<CardExtraSummaryItem
									key={`${selectedService.serviceId}/${index}`}
									icon={selectedService.icon}
									label={selectedService.label}
									status={selectedService.status}
									price={selectedService.price}
								/>
							))}
						</CardExtraSummaryGroup>
					))}
				</div>
			)}
		</>
	);

	const onClearError = useCallback(() => {
		setFormError('');
	}, []);

	const onUpdateUser = useCallback(() => {
		onCloseLoyalty();
		onCloseLinkCards();
		updateUser?.();
	}, [updateUser, onCloseLoyalty, onCloseLinkCards]);

	const onViewLoyaltyStatus = useCallback(() => {
		if (!loyaltyProgramAccountUrl) {
			Log({ level: 'error', message: 'loyaltyProgramAccountUrl is not set' });

			return;
		}

		window.open(loyaltyProgramAccountUrl, '_blank');
	}, [loyaltyProgramAccountUrl]);

	const onSubmit = async ({ travellers: travellerFields }: TravellerCardValues) => {
		const updateTravellers: InputTraveller[] = [];

		const travellersValuesMap = new Map(Object.entries(travellers).map(([id, { values }]) => [id, values]));

		Object.entries(travellerFields).forEach(([id, travellerFields]) => {
			const travellerValues = travellersValuesMap.get(id);

			if (travellerValues.loyaltyNumber || !travellerFields.loyaltyCardNumber) {
				return;
			}

			const isCorporateCard = travellerFields.loyaltyCardType === LoyaltyCardsType.CorporateCard;
			const loyaltyNumber = isCorporateCard
				? getCorporateNumber(travellerFields.loyaltyCardNumber)
				: travellerFields.loyaltyCardNumber;

			updateTravellers.push({ id, values: [{ type: TravellerFieldEnum.LoyaltyNumber, value: loyaltyNumber }] });
		});

		if (!updateTravellers.length) {
			onUpdateUser();

			return;
		}

		try {
			const { data } = await setTravellersData({
				variables: {
					variables: {
						orderId,
						travellersData: updateTravellers
					}
				}
			});

			if (!!data.SetTravellersData) {
				onUpdateUser();

				dispatch(fillOrder(data.SetTravellersData));
			}
		} catch (error) {
			setFormError(t(formatError(error)));
		}
	};

	const CardExtra = variant === 'aggregator' ? CardExtraViewAggregator : CardExtraView;

	if (!isFfpSupported) {
		return null;
	}

	return (
		<>
			<CardExtra
				type="loyalty"
				className={cn({ [loyaltyTheme.loyalty]: variant === 'aggregator' })}
				icon={<CardExtraIcon type="loyalty" />}
				title={loyaltyProgramName}
				description={
					isExistI18nTranslate('Description.Loyalty', { ns: 'ServiceCard' })
						? t('Description.Loyalty', { ns: 'ServiceCard' })
						: undefined
				}
				content={content}
				footerControls={
					<Button
						variant="optionAction"
						onClick={isFilledAllLoyaltyCards ? onViewLoyaltyStatus : onOpenLinkCards}
					>
						{t(isFilledAllLoyaltyCards ? 'My miles' : 'Add ffp cards', { ns: 'Checkout' })}
					</Button>
				}
				// aggregator props
				headerIcons={<CardExtraAggregatorHeaderIcon type="loyalty" />}
				onAdd={!isSelectedLoyaltyCards ? onOpenLinkCards : undefined}
			/>

			<LinkCardsModal
				userInfo={userInfo}
				travellers={orderTravellersWithLoyalty}
				hasLoyaltyProgramURL={!!loyaltyProgramAccountUrl}
				error={formError}
				isOpen={isOpenLinkCards}
				isLoading={loading}
				onClose={onCloseLinkCards}
				onSubmit={onSubmit}
				onClearError={onClearError}
				onOpenConnectModal={onOpenLoyalty}
				onGoToAccountLoyalty={onViewLoyaltyStatus}
			/>

			<LoyaltyConnect
				authType="authenticate"
				userInfo={getUserInfo(userInfo)}
				isOpen={isOpenLoyalty}
				onClose={onCloseLoyalty}
				onLoyaltyCardSaved={onUpdateUser}
				onGoToSettings={onGoToAccountSettings}
				showRegisterAction
			/>
		</>
	);
};

export default LoyaltyOrderCard;
